/**
 * The Azure Storage rows of the connection form's Storage section (spec §4.1; Option C, C2 mode-first order):
 * Connect using → the Storage account / Resource URL field → the share-path choice → sign-in (the shared rows) → Data
 * access → Services → File shares. The per-service endpoint overrides are in More options (`AzureStorageEndpointsRow`).
 *
 * What it READS comes from `azureStorageFormRules.ts` and what it WRITES from `azureStoragePreset.ts`, so the dialog
 * and the unit tests agree on what each control means.
 */
import type { ReactNode } from 'react';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type { StorageService } from '@/types/azureStorage';
import type { ConnectionFormValues } from './connectionFormSchema';
import {
  azuriteDockerRunParts,
  STORAGE_SERVICES,
  STORAGE_SERVICE_LABELS,
  storageEnabledServices,
  storageModeOnScreen,
  storageModePick,
  toggleStorageService,
  type StorageConnectMode,
} from './azureStorageFormRules';
import { storageModeValues } from './azureStoragePreset';
import { AzureStorageModeFields, AzureStorageModeHint } from './AzureStorageModeFields';
import { AzureStorageDataAccessFields, storageShowsDataAccess } from './AzureStorageDataAccessFields';
import { AzureStorageSharePathChoice } from './AzureStorageSharePathChoice';
import { AzureStorageNamedSharesField } from './AzureStorageNamedSharesField';
import { storageShowsNamedShares } from './azureStorageNamedShares';
import { FormRow } from '../connection-dialog/pane/FormRow';
import { CHECKBOX_CLASS, type ConnectionFormApi } from '../connection-dialog/fields/fieldControls';
import { STORAGE_SERVICES_REQUIRED } from '../connection-dialog/state/blockingIssue';

type Values = Partial<ConnectionFormValues>;

type Props = {
  form: ConnectionFormApi;
  /** The Storage account / Resource URL row, labelled for the mode on screen, with the mode's line under the field. */
  serverRow: (mode: StorageConnectMode, modeHint: ReactNode) => ReactNode;
  /** The shared sign-in rows (Authentication, the key / SAS / client, the tenant). */
  signInRows: ReactNode;
};

export function AzureStorageConnectionFields({ form, serverRow, signInRows }: Props) {
  return (
    <form.Subscribe selector={(state: { values: Values }) => state.values}>
      {(values: Values) => <StorageRows form={form} values={values} serverRow={serverRow} signInRows={signInRows} />}
    </form.Subscribe>
  );
}

function StorageRows({ form, values, serverRow, signInRows }: Props & { values: Values }) {
  const { track } = useAnalytics();
  // "Single resource" is a user intent before it is a value (its scope is written only once a resource URL is pasted):
  // the pick is a transient form VALUE, held while the values still read what they read right after it
  // (`storageModeOnScreen`) — so it survives an engine round trip and a paste for another engine (C3; the C2 review
  // C2-06 case, emulator → Single resource, still holds because the pick records what it wrote).
  const anonymous = values.authentication === 'Anonymous';
  const mode = storageModeOnScreen(values);
  const emulator = mode === 'emulator';
  const enabled = storageEnabledServices(values);

  const applyMode = (next: StorageConnectMode) => {
    const written = storageModeValues(next);
    for (const [field, value] of Object.entries(written)) {
      form.setFieldValue(field, value);
    }
    form.setFieldValue('azureStorageModePick', storageModePick(next, { ...values, ...written }));
    track('ui:storage_form_mode', { mode: next });
  };

  const setService = (service: StorageService, next: boolean) => {
    form.setFieldValue('azureStorageServices', toggleStorageService(enabled, service, next));
    track('ui:storage_form_service_toggle', { service, enabled: next });
  };

  // The emulator's command publishes the ports these endpoints dial (a remapped 20000 included), not always 10000–10002.
  const emulatorCommand = azuriteDockerRunParts(values.azureStorageEndpoints);
  const modeHint = (
    <AzureStorageModeHint
      mode={mode}
      scope={values.azureStorageScope}
      emulatorCommand={emulatorCommand}
      onCopyEmulatorCommand={() => {
        void navigator.clipboard?.writeText?.(emulatorCommand.join(' '));
        track('ui:storage_azurite_copy_command');
      }}
    />
  );

  return (
    <>
      <FormRow row="storageConnectMode" label="Connect using" groupId="storage-connect-mode-label">
        <AzureStorageModeFields mode={mode} anonymous={anonymous} onPick={applyMode} />
      </FormRow>
      {serverRow(mode, modeHint)}
      {mode === 'resource' && <AzureStorageSharePathChoice form={form} values={values} />}
      {signInRows}
      {storageShowsDataAccess(values, mode === 'account') && (
        <AzureStorageDataAccessFields form={form} values={values} onModeChange={(next) => track('ui:storage_form_data_access', { mode: next })} />
      )}
      <FormRow
        row="storageServices"
        label="Services"
        groupId="storage-services-label"
        hint={
          <>
            {/* C2 N7 (c10): the unavailable File shares box says why on screen, not only in its hover title. */}
            {emulator && <span className="block" data-testid="storage-service-file-reason">Azurite has no Files service, so File shares is unavailable.</span>}
            A service the account or credential cannot reach is reported by Test connection.
          </>
        }
      >
        <div className="flex min-h-9 flex-wrap items-center gap-x-5 gap-y-1">
          {STORAGE_SERVICES.map((service) => {
            const unavailable = emulator && service === 'file';
            return (
              <label key={service} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className={CHECKBOX_CLASS}
                  checked={!unavailable && enabled.includes(service)}
                  disabled={unavailable}
                  title={unavailable ? 'Azurite has no Files service' : undefined}
                  onChange={(event) => setService(service, event.target.checked)}
                  data-testid={`storage-service-${service}`}
                />
                {STORAGE_SERVICE_LABELS[service]}
              </label>
            );
          })}
        </div>
        {enabled.length === 0 && (
          <p className="mt-1 text-sm text-destructive" data-testid="storage-services-error">
            {STORAGE_SERVICES_REQUIRED}
          </p>
        )}
      </FormRow>
      {storageShowsNamedShares(values, mode === 'account', enabled.includes('file')) && <AzureStorageNamedSharesField form={form} values={values} />}
    </>
  );
}
